import { useMutation, useQueryClient } from '@tanstack/react-query';
import { usePostHog } from 'posthog-js/react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import LANGUAGES from '@/Domain/components/Settings/ai/LANGUAGES';
import { useSubscription } from '@/Domain/components/SubscriptionProvider';
import { PostHogEventEnum } from '@/Domain/enum/PostHogEvent.enum';
import { useAutoSubmit } from '@/Domain/hooks/App/useAutoSave.hook';
import {
  AISettings,
  CREATIVITY_OPTIONS,
  IDENTITY_DEFAULTS,
  LINKS_OPTIONS,
  PERSONALITY_OPTIONS,
  PROFESSION_OPTIONS,
  REPLY_OPTIONS,
} from '@/UI/Identity/Identity.constants';
import type { SelectOption } from '@/UI/Identity/components/ControlledSelect';

export const useIdentitySettings = (settings: AISettings) => {
  const { t } = useTranslation(['identity', 'settings', 'common']);
  const queryClient = useQueryClient();
  const postHog = usePostHog();
  const { isActivated, getConfig, isFetched } = useSubscription();

  const form = useForm<AISettings>({ values: settings });

  const mutation = useMutation<Partial<AISettings>, unknown, Partial<AISettings>>({
    mutationKey: ['settings'],
    onMutate: (updated) => {
      const previous = queryClient.getQueryData<AISettings>(['settings']);
      // Autosave submits the whole form; report only the keys that actually changed so a tone/
      // persona/prompt edit is measurable without an event per keystroke on untouched fields.
      const changedFields = Object.keys(updated).filter(
        (key) => previous?.[key as keyof AISettings] !== updated[key as keyof AISettings],
      );
      if (changedFields.length) {
        postHog?.capture(PostHogEventEnum.IdentitySettingSaved, { fields: changedFields });
      }
      queryClient.setQueryData<AISettings>(['settings'], (old) =>
        old ? { ...old, ...updated } : old,
      );
      return { previous };
    },
    onError: (_error, _updated, context) => {
      const ctx = context as { previous?: AISettings } | undefined;
      if (ctx?.previous) queryClient.setQueryData(['settings'], ctx.previous);
    },
  });

  useAutoSubmit(form, (data) => mutation.mutate(data as Partial<AISettings>));

  const personalitiesCfg = getConfig('personalities') as Record<string, boolean> | undefined;
  const professionsCfg = getConfig('professions') as Record<string, boolean> | undefined;
  const aiPreferencesLocked = isFetched && !isActivated('ai_preferences');

  const defaultTag = ` (${t('common:default', { defaultValue: 'Default' })})`;
  const withDefault = (label: string, isDefault: boolean) =>
    isDefault ? label + defaultTag : label;

  const personalityOptions: SelectOption[] = PERSONALITY_OPTIONS.map(({ value, key }) => ({
    value: String(value),
    label: withDefault(t(`common:personalities.${key}`), value === IDENTITY_DEFAULTS.personality),
    description: t(`settings:personality.descriptions.${value}`),
    disabled: personalitiesCfg ? !personalitiesCfg[key] : false,
    tooltip: t(`identity:personality_examples.${key}`),
  }));
  const professionOptions: SelectOption[] = PROFESSION_OPTIONS.map((prof) => ({
    value: prof,
    label: withDefault(
      t(`settings:profession.options.${prof}`),
      prof === IDENTITY_DEFAULTS.profession,
    ),
    description: t(`settings:profession.descriptions.${prof}`),
    disabled: professionsCfg ? !professionsCfg[prof] : false,
  }));
  const creativityOptions: SelectOption[] = CREATIVITY_OPTIONS.map((v) => ({
    value: String(v),
    label: withDefault(
      t(`settings:creativity.options.${v}`),
      v === IDENTITY_DEFAULTS.creativity_level,
    ),
    description: t(`settings:creativity.descriptions.${v}`),
  }));
  const replyOptions: SelectOption[] = REPLY_OPTIONS.map((v) => ({
    value: String(v),
    label: withDefault(t(`settings:reply.options.${v}`), v === IDENTITY_DEFAULTS.reply_length),
    description: t(`settings:reply.descriptions.${v}`),
  }));
  const linksOptions: SelectOption[] = LINKS_OPTIONS.map((v) => ({
    value: String(v),
    label: withDefault(
      t(`settings:links_priority.options.${v}`),
      v === IDENTITY_DEFAULTS.url_priority,
    ),
    description: t(`settings:links_priority.options_description.${v}`),
  }));
  const languageOptions: SelectOption[] = LANGUAGES.map((code) => ({
    value: String(code),
    label: withDefault(
      t(`languages.${code}`, { ns: 'common' }),
      code === IDENTITY_DEFAULTS.language,
    ),
  })).sort((a, b) => a.label.localeCompare(b.label));

  return {
    form,
    aiPreferencesLocked,
    personalityOptions,
    professionOptions,
    creativityOptions,
    replyOptions,
    linksOptions,
    languageOptions,
  };
};
